<script setup>
import SkeletonPlaceholder from '@/components/Base/SkeletonPlaceholder.vue'

const props = defineProps({
    baseButtonConfig: {
        type: Object,
        default: () => ({}),
        validator: (rawValue) => {
            const validations = {
                type: value => value === undefined || (typeof value === 'string' && ['submit', 'external', 'file', 'div', 'internal'].includes(value)),
                buttonType: value => value === undefined || (typeof value === 'string' && ['primary', 'secondary'].includes(value)),
                active: value => value === undefined || typeof value === 'boolean',
                large: value => value === undefined || typeof value === 'boolean',
                isPending: value => value === undefined || typeof value === 'boolean',
                disabled: value => value === undefined || typeof value === 'boolean',
                link: value => value === undefined || typeof value === 'string',
                fn: value => value === undefined || typeof value === 'function',
                placeholder: value => value === undefined || typeof value === 'boolean'
            }

            return Object.entries(validations).every(([key, validate]) => validate(rawValue[key]))
        }
    }
})

// Compute if the button type is 'submit'
const isSubmitBtn = computed(() => props.baseButtonConfig?.type === 'submit')

// Compute if the button type is 'external'
const isExternalLink = computed(() => props.baseButtonConfig?.type === 'external')

// Compute if the button type is 'file'
const isFileDownloadLink = computed(() => props.baseButtonConfig?.type === 'file')

// Compute if the button type is 'div'
const isDiv = computed(() => props.baseButtonConfig?.type === 'div')

// Compute target attribute based on external link
const target = computed(() => isExternalLink.value ? '_blank' : null)

// Compute rel attribute based on external link
const rel = computed(() => isExternalLink.value ? 'noopener noreferrer' : null)

// Compute download attribute based on file download link
const download = computed(() => isFileDownloadLink.value ? 'download' : null)
const getDownloadName = computed(() => download.value ? props.baseButtonConfig?.link?.split('/').pop() : null)

// Compute type attribute based on submit button
const type = computed(() => isSubmitBtn.value ? 'submit' : null)

const buttonClass = computed(() => {
    if (!props.baseButtonConfig) return {}

    return {
        'btn-active': props?.baseButtonConfig?.active,
        'btn-primary': props?.baseButtonConfig?.buttonType !== 'secondary',
        'btn-secondary': props?.baseButtonConfig?.buttonType === 'secondary',
        'btn-large': props?.baseButtonConfig?.large,
        'btn-pending': props?.baseButtonConfig?.isPending
    }
})
const component = computed(() => isSubmitBtn.value ? 'button' : isDiv.value ? 'div' : resolveComponent('NuxtLinkLocale'))
</script>

<template>
    <component
        :is="SkeletonPlaceholder"
        v-if="baseButtonConfig?.placeholder"
        :skeleton-placeholder-config="{ type: 'button' }"
    />
    <component
        :is="component"
        v-else
        :to="baseButtonConfig.link"
        :class="buttonClass"
        :target="target"
        :rel="rel"
        :download="getDownloadName"
        :type="type"
        :disabled="baseButtonConfig.disabled"
        @click="baseButtonConfig.fn"
    >
        <span><slot /></span>
    </component>
</template>

<style lang="scss">
.btn-primary {
    @include btn($type: 'primary');
}

.btn-secondary {
    @include btn($type: 'secondary');
}
</style>
